import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Info, X } from 'lucide-react';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { ShopifyEditorLinkDto } from '@/Domain/api/Shopify/dto/ShopifyEditorLink.dto';
import { AiInvitation } from '@/Domain/components/Ai/AiInvitation';
import {
  OnboardingActionCards,
  PostOnboardingChecklist,
} from '@/Domain/components/OnboardingProgress';
import { Alert, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  PostHogEventEnum,
  PostHogFeatureFlagEnum,
  PostHogUserPropertyEnum,
} from '@/Domain/enum/PostHogEvent.enum';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { AdvancedSettings } from '@/UI/AiPreview/AdvancedSettings';
import { AiWidget } from '@/UI/AiPreview/AiWidget';
import {
  greetingFirstName,
  renderWelcomeTitle,
} from '@/UI/GettingStarted/components/AutomaticOnboarding';
import NewWidget from '@/UI/Website/NewWidget/NewWidget';
import { CHAT_PAGE_ACCESS_TOKEN_KEY, getPreviewIdentityToken, SCENARIO_ID } from '@/context';

const SONY_SCENARIO_ID = '9crbw4zgit';

export const AiPreviewPage = () => {
  const postHog = usePostHog();
  const { t } = useTranslation('ai_preview_page');
  const { user } = useUser();
  const greetingName = greetingFirstName(user?.first_name);
  const [newsVisible, setNewsVisible] = useState(false);
  const isNewsEnabled = useFeatureFlagEnabled(PostHogFeatureFlagEnum.AiPreviewNews);
  const isNewWidgetEnabled = SCENARIO_ID !== SONY_SCENARIO_ID;

  const handleCloseNews = useCallback(
    (event: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
      postHog.capture(PostHogEventEnum.AiPreviewNewsClose);
      postHog.people.set({ [PostHogUserPropertyEnum.AiPreviewNews]: true });

      postHog.reloadFeatureFlags();
      setNewsVisible(false);
    },
    [postHog],
  );

  const { data: shopifyEditorLink } = useQuery<ShopifyEditorLinkDto>({
    queryKey: [QueryKey.shopify.editorLink],
  });

  const isShopifyUser = !!shopifyEditorLink?.editor_url;

  const handleOpenShopifyLink = () => window.open(shopifyEditorLink?.editor_url);

  useEffect(() => {
    setNewsVisible(!!isNewsEnabled);
  }, [isNewsEnabled]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col space-y-5 h-full max-w-7xl w-full mx-auto"
    >
      <AiInvitation />

      <AnimatePresence>
        {newsVisible && (
          <motion.div
            key="preview-news-alert"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert variant="info">
              <AlertTitle
                endIcon={
                  <Button
                    className="ml-auto !border-none"
                    variant="custom"
                    size="icon"
                    onClick={handleCloseNews}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                }
              >
                <Info className="h-4 w-4" />
                <span className="ml-2 text-sm">
                  <Trans
                    i18nKey={t('news.title')}
                    t={t}
                    components={{
                      analytics_link: (
                        <Link
                          className="font-semibold"
                          to="insights"
                          relative="path"
                          onClick={handleCloseNews}
                        />
                      ),
                    }}
                  />
                </span>
              </AlertTitle>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="h-full flex-1 grid grid-cols-1 lg:grid-cols-2 rounded-2xl items-center lg:gap-6 xl:gap-10">
        <div className="ring-1 ring-border rounded-3xl z-10 flex items-center start 2xl:justify-center h-[calc(100vh-140px)] max-h-[60rem] max-w-[80rem] relative overflow-hidden">
          {isNewWidgetEnabled ? (
            <NewWidget
              showViewModeToggle={false}
              fixedViewType="embed"
              source="preview"
              customParams={
                getPreviewIdentityToken()
                  ? { [CHAT_PAGE_ACCESS_TOKEN_KEY]: getPreviewIdentityToken() }
                  : undefined
              }
            />
          ) : (
            <AiWidget />
          )}

          <AdvancedSettings />

          {/* Intentionally no retrain / setup-required overlay here.
              Previously this spot rendered a blurred-out overlay that blocked
              the widget whenever `knowledge_base.retrain_state` was
              `to_be_retrained` / `in_progress`, with a "Retrain" button. The
              new onboarding flow fires a fire-and-forget recursive site crawl
              after the first-page scrape, which flips the KB into
              `in_progress` the moment the user lands on AI Preview — blocking
              the widget in that window made the first-run experience feel
              broken ("I onboarded, why can't I chat?"). The retrain state is
              now surfaced non-blockingly in the TopBar (see `TopBar.tsx`), so
              the user can chat with the (already-trained) widget while the
              background crawl extends the knowledge base. Product decision,
              not a bug. */}
        </div>

        <div className="flex-col gap-6 hidden lg:flex z-10 w-full max-h-[calc(100vh-140px)] overflow-y-auto custom-scroll">
          <div className="flex flex-col gap-3">
            <h1 className="font-safiro text-4xl font-medium text-foreground text-balance leading-[1.1]">
              {greetingName
                ? renderWelcomeTitle(t('welcome_with_name', { name: greetingName }), greetingName)
                : t('welcome')}
            </h1>
            <h2 className="text-base font-medium text-muted-foreground">
              {t('preview_description')}
            </h2>
          </div>

          {isShopifyUser && (
            <div className="bg-card border border-border rounded-xl p-7">
              <div className="flex flex-col items-start gap-5">
                <img src="/static/assets/shopify-logo.webp" alt="Shopify" className="h-8 w-auto" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{t('pages.shopify_title')}</p>
                  <p className="text-xs text-foreground-subtle">{t('pages.shopify_description')}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleOpenShopifyLink}
                  className="w-full"
                >
                  {t('button.shopify_theme_editor')}
                  <ArrowUpRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <PostOnboardingChecklist />

          <OnboardingActionCards />
        </div>
      </main>
    </motion.div>
  );
};
